import { CurrencyCode as CurrencyCodeType } from "@smartrr/shared/currencyCode";
import {
  ISubscriptionProperties,
  PurchaseStateAndDelivery,
  isUpcomingDelivery,
} from "@smartrr/shared/entities/PurchaseState";
import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { CurrencyCode } from "@smartrr/shared/shopifyGraphQL/api";
import { getSymbolFromCurrency } from "@smartrr/shared/utils/formatMoney";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useMemo } from "react";
import { StateCreator, create } from "zustand";
import { ModalState } from "../utils/constants";
import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";
import { ISmartrrOrderFromDb } from "@smartrr/shared/entities/Order";
import { immer } from "zustand/middleware/immer";
import { useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import { SPECIAL_REFERRALS_NOT_ACTIVE_FLAG_VALUE, useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";
import { SmartrrApi } from "@customer-app/api";
import { StatusCodes } from "http-status-codes";
import isNumber from 'lodash/isNumber';
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { isDiscountSpecialReferrals } from "@customer-app/utils/specialReferrals";
import {
  DeliveryRedeemResult,
  OTPRedeemResult,
  RedeemedIncentive,
  RedeemedVariant,
  WithResult,
} from "./constants";
import { isRewardIncentive } from "../utils";
import { sortTiersByRank } from "@smartrr/shared/utils/sortBy";
import { getUsersTierFromTieredPoints } from "@smartrr/shared/interfaces/loyalty/utils";
import { YearlyTieredPoints } from "@smartrr/shared/entities/CustomerRelationship";

interface LoyaltyStore {
  /**
   * null: initial state
   * undefined: did not receive valid loyalty
   */
  loyalty: LoyaltyApi.Program.Type | null | undefined;
  referralLink: LoyaltyApi.ReferralCode.Type | null | undefined;
  upcomingOrder: PurchaseStateAndDelivery | null;
  upcomingPausedOrder: PurchaseStateAndDelivery | null;
  latestPurchasedOrder: ISmartrrOrderFromDb | null;
  cumulativePoints: number;
  tieredPoints: number;
  currency: CurrencyCodeType;
  isLoading: boolean;
  modalState: ModalState | string;
  discountCode: string;
  redeemedVariants: RedeemedVariant[];
  redeemedIncentives: RedeemedIncentive[];

  tiers: {
    previous: LoyaltyApi.Tier.Type | null;
    current: LoyaltyApi.Tier.Type | null;
    next: LoyaltyApi.Tier.Type | null;
  };

  actions: {
    fetchLoyalty(): Promise<void>;
    fetchReferralLink(): Promise<void>;
    points: {
      updateCumulativePoints(newPoints: number): void;
      updateTieredPoints(newPoints: number): void;
    };
    order: {
      updateUpcoming(newUpcomingOrder: PurchaseStateAndDelivery | null): void;
      updateUpcomingPaused(newUpcomingPausedOrder: PurchaseStateAndDelivery | null): void;
      updatePurchased(newPurchasedOrder: ISmartrrOrderFromDb | null): void;
      redeem: {
        variant(variant: LoyaltyApi.RewardProduct.Type): Promise<WithResult<DeliveryRedeemResult>>;
        incentive(incentive: LoyaltyApi.Incentive.Type): Promise<WithResult<DeliveryRedeemResult>>;
      };
    };
    tiers: {
      updateTier(yearlyTieredPoints: YearlyTieredPoints[] | undefined): void;
    };
    otp: {
      redeem: {
        variant(variant: LoyaltyApi.RewardProduct.Type): Promise<WithResult<OTPRedeemResult>>;
        incentive(incentive: LoyaltyApi.Incentive.Type): Promise<WithResult<OTPRedeemResult>>;
        setDiscountCode(discountCode: string): void;
        setRedeemedVariants(redeemedVariant: RedeemedVariant | []): void;
        setRedeemedIncentives(redeemedIncentive: RedeemedIncentive | []): void;
      };
    };
    currency: {
      update(newCurrency: CurrencyCodeType): void;
    };
    modal: {
      updateModalState(newModalState: ModalState | string): void;
    };
  };

  internal: {
    whileLoading<Type>(fn: () => Promise<Type>): Promise<Type>;
    initializeLoyalty(loyalty: LoyaltyApi.Program.Type | undefined);
  };
}

const loyaltyStoreSlice: StateCreator<LoyaltyStore, [["zustand/immer", never]]> = (set, get) => ({
  loyalty: null,
  referralLink: null,
  upcomingOrder: null,
  upcomingPausedOrder: null,
  latestPurchasedOrder: null,
  cumulativePoints: 0,
  tieredPoints: 0,
  isLoading: false,
  currency: CurrencyCode.Usd,
  modalState: "",
  discountCode: "",
  redeemedVariants: [],
  redeemedIncentives: [],
  tiers: {
    previous: null,
    current: null,
    next: null,
  },

  actions: {
    async fetchLoyalty(): Promise<void> {
      if (get().isLoading) {
        return;
      }

      await get().internal.whileLoading(async () => {
        if (get().loyalty !== null) {
          return;
        }

        const response = await SmartrrApi.loyalty.getLoyalty();

        if (response.status === StatusCodes.OK && LoyaltyApi.Program.schema.safeParse(response.body)) {
          get().internal.initializeLoyalty(response.body ?? undefined);
        } else {
          get().internal.initializeLoyalty(undefined);
        }
      });
    },
    async fetchReferralLink(): Promise<void> {
      if (!get().loyalty?.referralProgram) {
        set({
          referralLink: undefined,
        });
        return;
      }

      return await get().internal.whileLoading(async () => {
        if (get().referralLink !== null) {
          return;
        }

        const response = await typedCustomerFrontendApi.getReq("/loyalty/referral-link");
        if (response.type === "success" && LoyaltyApi.ReferralCode.schema.safeParse(response.body)) {
          set({
            referralLink: response.body ?? undefined,
          });
        } else {
          set({
            referralLink: undefined,
          });
        }
      });
    },
    points: {
      updateCumulativePoints(newPoints: number): void {
        set({
          cumulativePoints: newPoints,
        });
      },
      updateTieredPoints(newPoints: number): void {
        set({
          tieredPoints: newPoints,
        });
      },
    },
    order: {
      updateUpcoming(newUpcomingOrder: PurchaseStateAndDelivery | null): void {
        set({
          upcomingOrder: newUpcomingOrder,
        });
      },
      updateUpcomingPaused(newUpcomingPausedOrder: PurchaseStateAndDelivery | null): void {
        set({
          upcomingPausedOrder: newUpcomingPausedOrder,
        });
      },
      updatePurchased(newPurchasedOrder: ISmartrrOrderFromDb | null): void {
        set({
          latestPurchasedOrder: newPurchasedOrder,
        });
      },
      redeem: {
        async variant(variant: LoyaltyApi.RewardProduct.Type): Promise<WithResult<DeliveryRedeemResult>> {
          if (get().isLoading) {
            return {
              result: "failure",
              message: "Still loading",
            };
          }

          return await get().internal.whileLoading(async () => {
            const order = get().upcomingOrder;
            const upcomingPausedOrder = get().upcomingPausedOrder;
            if (!order) {
              if (upcomingPausedOrder) {
                const response = await typedCustomerFrontendApi.postReq(
                  "/loyalty/redeem/reward-product/:variantId/purchase-state/:cpsId",
                  {
                    params: {
                      cpsId: upcomingPausedOrder.purchaseState.id,
                      variantId: variant.id,
                    },
                  }
                );

                if (response.type === "success") {
                  return {
                    result: "success",
                    data: {
                      orgPoints: response.body.orgPoints ?? 0,
                      updatedCPS: response.body.updatedCPS ?? null,
                    },
                  };
                }
                return {
                  result: "failure",
                  message: response.message ?? "Error redeeming product",
                };
              }

              return {
                result: "failure",
                message: "No upcoming order",
              };
            }

            const response = await typedCustomerFrontendApi.postReq(
              "/loyalty/redeem/reward-product/:variantId/purchase-state/:cpsId",
              {
                params: {
                  cpsId: order.purchaseState.id,
                  variantId: variant.id,
                },
              }
            );

            if (response.type === "success") {
              return {
                result: "success",
                data: {
                  orgPoints: response.body.orgPoints ?? 0,
                  updatedCPS: response.body.updatedCPS ?? null,
                },
              };
            }
            return {
              result: "failure",
              message: response.message ?? "Error redeeming product",
            };
          });
        },
        async incentive(incentive: LoyaltyApi.Incentive.Type): Promise<WithResult<DeliveryRedeemResult>> {
          if (get().isLoading) {
            return {
              result: "failure",
              message: "Still loading",
            };
          }

          return await get().internal.whileLoading(async () => {
            const order = get().upcomingOrder;
            const upcomingPausedOrder = get().upcomingPausedOrder;
            if (!order) {
              if (upcomingPausedOrder) {
                const response = await typedCustomerFrontendApi.postReq(
                  "/loyalty/redeem/incentive/:incentiveId/purchase-state/:cpsId",
                  {
                    params: {
                      cpsId: upcomingPausedOrder.purchaseState.id,
                      incentiveId: incentive.id,
                    },
                  }
                );

                if (response.type === "success") {
                  return {
                    result: "success",
                    data: {
                      orgPoints: response.body.orgPoints ?? 0,
                      updatedCPS: response.body.updatedCPS ?? null,
                    },
                  };
                }
                return {
                  result: "failure",
                  message: response.message ?? "Error redeeming product",
                };
              }
              return {
                result: "failure",
                message: "No upcoming order",
              };
            }

            const response = await typedCustomerFrontendApi.postReq(
              "/loyalty/redeem/incentive/:incentiveId/purchase-state/:cpsId",
              {
                params: {
                  cpsId: order.purchaseState.id,
                  incentiveId: incentive.id,
                },
              }
            );

            if (response.type === "success") {
              return {
                result: "success",
                data: {
                  orgPoints: response.body.orgPoints ?? 0,
                  updatedCPS: response.body.updatedCPS ?? null,
                },
              };
            }
            return {
              result: "failure",
              message: response.message ?? "Error redeeming product",
            };
          });
        },
      },
    },
    tiers: {
      updateTier(yearlyTieredPoints) {
        const orgTiers = get().loyalty?.tiers;
        const storedTierPoints = get().tieredPoints;
        if (!orgTiers || !yearlyTieredPoints) {
          return;
        }

        const currentYear = new Date().getFullYear();
        const previousYearEntry = yearlyTieredPoints.find(entry => entry.year === currentYear - 1);
        const currentYearEntry = yearlyTieredPoints.find(entry => entry.year === currentYear);

        const tieredPoints = currentYearEntry?.points ?? storedTierPoints;
        const previousYearTieredPoints = previousYearEntry?.points;

        const { currentYearTier, previousYearTier, nextTier } = getUsersTierFromTieredPoints({
          orgTiers,
          tieredPoints,
          previousYearTieredPoints,
        });

        set(draft => {
          draft.tiers = {
            previous: previousYearTier,
            current: currentYearTier,
            next: nextTier,
          };
        });
      },
    },
    otp: {
      redeem: {
        async variant(variant: LoyaltyApi.RewardProduct.Type): Promise<WithResult<OTPRedeemResult>> {
          if (get().isLoading) {
            return {
              result: "failure",
              message: "Still loading",
            };
          }

          return await get().internal.whileLoading(async () => {
            const response = await typedCustomerFrontendApi.postReq(
              "/loyalty/otp/redeem/reward-product/:variantId",
              {
                params: {
                  variantId: variant.id,
                },
              }
            );

            if (response.type === "success") {
              this.setDiscountCode(response.body.discountCode);
              this.setRedeemedVariants({ id: variant.id, discountCode: response.body.discountCode });
              return {
                result: "success",
                data: {
                  orgPoints: response.body.orgPoints ?? 0,
                  discountCode: response.body.discountCode ?? "",
                },
              };
            }
            return {
              result: "failure",
              message: response.message ?? "Error redeeming product",
            };
          });
        },
        async incentive(incentive: LoyaltyApi.Incentive.Type): Promise<WithResult<OTPRedeemResult>> {
          if (get().isLoading) {
            return {
              result: "failure",
              message: "Still loading",
            };
          }

          return await get().internal.whileLoading(async () => {
            const response = await typedCustomerFrontendApi.postReq(
              "/loyalty/otp/redeem/incentive/:incentiveId",
              {
                params: {
                  incentiveId: incentive.id,
                },
              }
            );

            if (response.type === "success") {
              this.setRedeemedIncentives({ id: incentive.id, discountCode: response.body.discountCode });
              return {
                result: "success",
                data: {
                  orgPoints: response.body.orgPoints ?? 0,
                  discountCode: response.body.discountCode ?? "",
                },
              };
            }
            return {
              result: "failure",
              message: response.message ?? "Error redeeming incentive",
            };
          });
        },
        setDiscountCode(discountCode: string) {
          set({
            discountCode,
          });
        },
        setRedeemedVariants(redeemedVariant: RedeemedVariant) {
          set(draft => {
            if (!draft.redeemedVariants.some(variant => variant.id === redeemedVariant.id)) {
              draft.redeemedVariants = [...draft.redeemedVariants, redeemedVariant];
            }
          });
        },
        setRedeemedIncentives(redeemedIncentive: RedeemedIncentive) {
          set(draft => {
            if (!draft.redeemedIncentives.some(incentive => incentive.id === redeemedIncentive.id)) {
              draft.redeemedIncentives = [...draft.redeemedIncentives, redeemedIncentive];
            }
          });
        },
      },
    },
    currency: {
      update(newCurrency: CurrencyCodeType): void {
        set({
          currency: newCurrency,
        });
      },
    },
    modal: {
      updateModalState(newModalState: ModalState): void {
        set({
          modalState: newModalState,
        });
      },
    },
  },
  internal: {
    async whileLoading<Type>(fn: () => Promise<Type>): Promise<Type> {
      set({
        isLoading: true,
      });
      const response = await fn();
      set({
        isLoading: false,
      });
      return response;
    },
    initializeLoyalty(loyalty: LoyaltyApi.Program.Type | undefined) {
      if (get().loyalty !== null) {
        return;
      }
      set(draft => {
        draft.loyalty = loyalty;
      });
    },
  },
});

const useLoyaltyStore = create<LoyaltyStore>()(immer(loyaltyStoreSlice));
// TODO: Move LoyaltyStoreAccess to dedicated file because this is getting a bit long
export const LoyaltyStoreAccess = {
  useReferralLink(): LoyaltyApi.ReferralCode.Type | null {
    return useLoyaltyStore(state => state.referralLink) ?? null;
  },
  usePoints(): { cumulativePoints: number; tieredPoints: number } {
    const cumulativePoints = useLoyaltyStore(state => state.cumulativePoints);
    const tieredPoints = useLoyaltyStore(state => state.tieredPoints);
    return { cumulativePoints, tieredPoints };
  },
  useOrder(): PurchaseStateAndDelivery | null {
    return useLoyaltyStore(state => state.upcomingOrder);
  },
  useActions(): LoyaltyStore["actions"] {
    return useLoyaltyStore(state => state.actions);
  },
  useIsLoading(): boolean {
    return useLoyaltyStore(state => state.isLoading);
  },
  useCurrency(): { currency: CurrencyCodeType; symbol: string } {
    const currency = useLoyaltyStore(state => state.currency);
    const symbol = getSymbolFromCurrency(currency);
    return { currency, symbol };
  },
  useUnpauseModalState(): ModalState | string {
    return useLoyaltyStore(state => state.modalState);
  },
  useDiscountCode(): string {
    return useLoyaltyStore(state => state.discountCode);
  },
  useRedeemedIncentives(): RedeemedIncentive[] | [] {
    return useLoyaltyStore(state => state.redeemedIncentives);
  },
  useRedeemedVariants(): RedeemedVariant[] | [] {
    return useLoyaltyStore(state => state.redeemedVariants);
  },
  useIsLoyaltyEnabled(): boolean {
    const loyalty = useLoyaltyStore(state => state.loyalty);
    return useMemo(() => {
      return (
        loyalty !== undefined &&
        loyalty !== null &&
        loyalty.status === "ENABLED" &&
        (loyalty.incentives.length > 0 || loyalty.variants.length > 0)
      );
    }, [loyalty]);
  },
  useIsTiersEnabled(): boolean {
    const tiersEnabled = useLoyaltyStore(state => state.loyalty?.tiersEnabled);
    const { allTiers } = LoyaltyStoreAccess.useTiers();
    return useMemo(() => {
      return !!(allTiers?.length && tiersEnabled);
    }, [allTiers, tiersEnabled]);
  },

  /**
   * Used for determining if a shop has special referral program enabled and what variant is tied to the shop
   *
   * Note: We are not checking whether the variant gid provided through LD is an actual variant for the specific shop
   *
   * `undefined` - Not Active
   * `string` - Active, Shopify Variant gid
   */
  useSpecialReferralsVariantId(): string | undefined {
    const specialReferralsFlag = useSmartrrFlags().specialReferrals;
    if (specialReferralsFlag === SPECIAL_REFERRALS_NOT_ACTIVE_FLAG_VALUE) {
      return undefined;
    }
    return specialReferralsFlag;
  },
  /**
   * Used for determining if a specific variant is apart of a shop's special referral program and is valid relative to the schedule's indexFromNext
   *
   * `specialReferralsVariantId` is derived from LaunchDarkly
   */
  useIsDiscountReferralsVariant(
    item: IPurchaseStateLineItem,
    indexFromNext: number,
    subProperties: ISubscriptionProperties | null | undefined
  ): boolean {
    const { shopifyId: vntShopifyId } = item.vnt;
    const specialReferralsVariantId = LoyaltyStoreAccess.useSpecialReferralsVariantId();

    const hasVariantIdIssue = !vntShopifyId || !specialReferralsVariantId;

    const remainingSpecialRewards = subProperties?.remainingSpecialRewards;
    const isRemainingSpecialRewardsValid = isNumber(remainingSpecialRewards) && remainingSpecialRewards >= 0;

    const isUpcoming = isUpcomingDelivery(indexFromNext);
    const hasSpecialReferralsDiscount = (item.discounts ?? []).some(discount =>
      isDiscountSpecialReferrals(discount)
    );
    const isRedeemedLineItem = item.isRedeemed;

    // If upcoming order then discount is already applied and is valid
    // If future order, we need to ensure remainingSpecialRewards >= 0
    const orderHasSpecialRewards = isUpcoming ? true : isRemainingSpecialRewardsValid;

    if (hasVariantIdIssue || !hasSpecialReferralsDiscount || !orderHasSpecialRewards || !isRedeemedLineItem) {
      return false;
    }

    const doVariantIdsMatch = specialReferralsVariantId === vntShopifyId;

    // If isUpcoming order, we don't need to check if there are any remaining rewards available - that check will only apply for future orders
    return doVariantIdsMatch && (isUpcoming || indexFromNext <= remainingSpecialRewards!);
  },
  useReferralProgram(): LoyaltyApi.Referral.Type | null {
    return useLoyaltyStore(state => state.loyalty?.referralProgram) ?? null;
  },
  useRewardSettings(): LoyaltyApi.Settings.Type | null {
    return useLoyaltyStore(state => state.loyalty?.rewardSettings) ?? null;
  },
  useMultiplierCurrency(): CurrencyCodeType {
    const upcomingOrder = useLoyaltyStore(state => state.upcomingOrder);
    const latestPurchasedOrder = useLoyaltyStore(state => state.latestPurchasedOrder);
    const organization = useOrganization();

    return useMemo((): CurrencyCodeType => {
      return (
        upcomingOrder?.purchaseState.currency ??
        latestPurchasedOrder?.orderCurrency ??
        organization.shopifyStoreData?.currency ??
        CurrencyCode.Usd
      );
    }, [organization, upcomingOrder, latestPurchasedOrder]);
  },
  useMultiplier(): number {
    const currencies = useLoyaltyStore(state => state.loyalty?.currencies);
    const multiplierCurrency = LoyaltyStoreAccess.useMultiplierCurrency();

    return useMemo(() => {
      if (currencies) {
        return currencies.find(c => c.currency === multiplierCurrency)?.multiplier ?? 1;
      }
      return 1;
    }, [currencies, multiplierCurrency]);
  },
  useIncentives(): LoyaltyApi.Program.Type["incentives"] {
    const incentives = useLoyaltyStore(state => state.loyalty?.incentives);
    const multiplier = LoyaltyStoreAccess.useMultiplier();

    return useMemo(() => {
      return (incentives ?? []).map(incentive => ({
        ...incentive,
        costInPoints: Math.max(1, Math.ceil(incentive.costInPoints * multiplier)),
      }));
    }, [incentives, multiplier]);
  },
  useVariants(): LoyaltyApi.Program.Type["variants"] {
    const variants = useLoyaltyStore(state => state.loyalty?.variants);
    const multiplier = LoyaltyStoreAccess.useMultiplier();

    return useMemo(() => {
      return (variants ?? []).map(variant => ({
        ...variant,
        costInPoints: Math.max(1, Math.ceil(variant.costInPoints * multiplier)),
      }));
    }, [variants, multiplier]);
  },
  useAllRewardItems() {
    const allIncentives = this.useIncentives();
    const allVariants = this.useVariants();
    return useMemo(() => [...allIncentives, ...allVariants], [allIncentives, allVariants]);
  },
  useIsOtpEnabled(): boolean {
    return useLoyaltyStore(state => state.loyalty?.otpEnabled) ?? false;
  },
  useEvents(): LoyaltyApi.Program.Type["events"] | null {
    return useLoyaltyStore(state => state.loyalty?.events) ?? null;
  },
  useBonus(): LoyaltyApi.Program.Type["bonus"] | null {
    return useLoyaltyStore(state => state.loyalty?.bonus) ?? null;
  },
  useActiveSubscriberMultiplier(): number | null {
    return useLoyaltyStore(state => state.loyalty?.activeSubscriberMultiplier) ?? null;
  },
  useDescription(): string {
    return useLoyaltyStore(state => state.loyalty?.description) ?? "";
  },

  useSuffix(value: number): string {
    const pluralName = useLoyaltyStore(state => state.loyalty?.rewardUnitNamePlural) ?? "";
    const singularName = useLoyaltyStore(state => state.loyalty?.rewardUnitNameSingular) ?? "";

    const plural =
      typedLocalize("loyalty.unitPlural") === "points" ? pluralName : typedLocalize("loyalty.unitPlural");
    const singular =
      typedLocalize("loyalty.unitSingular") === "point" ? singularName : typedLocalize("loyalty.unitSingular");

    return useMemo(() => {
      return (value === 1 ? singular : plural) ?? "";
    }, [pluralName, singularName, value]);
  },
  useText(value: number): string {
    const suffix = LoyaltyStoreAccess.useSuffix(value);

    return `${value} ${suffix}`;
  },

  useRewardUnits() {
    const pluralUnit = useLoyaltyStore(state => state.loyalty?.rewardUnitNamePlural) ?? "";
    const singularUnit = useLoyaltyStore(state => state.loyalty?.rewardUnitNameSingular) ?? "";
    return useMemo(() => ({ singularUnit, pluralUnit }), [singularUnit, pluralUnit]);
  },

  useTiers(): {
    allTiers: LoyaltyApi.Tier.Type[];
    currentTier: LoyaltyApi.Tier.Type | null;
    previousTier: LoyaltyApi.Tier.Type | null;
    nextTier: LoyaltyApi.Tier.Type | null;
  } {
    const { current, next, previous } = useLoyaltyStore(state => state.tiers);
    const allTiers = useLoyaltyStore(state => state.loyalty?.tiers) ?? [];

    return { previousTier: previous, currentTier: current, nextTier: next, allTiers };
  },

  useTiersContainingReward(id: string): LoyaltyApi.Tier.Type[] | undefined {
    const { allTiers } = this.useTiers();
    const allRewardItems = this.useAllRewardItems();
    const rewardItem = useMemo(() => allRewardItems.find(item => item.id === id), [id]);

    return useMemo(() => {
      if (!rewardItem || !allTiers?.length) {
        return;
      }

      const tiers = isRewardIncentive(rewardItem)
        ? allTiers.filter(tier => tier.incentives?.some(incId => incId === rewardItem.id))
        : allTiers.filter(tier => tier.products?.some(p => p === rewardItem.variantId));

      return sortTiersByRank(tiers);
    }, [id, allTiers]);
  },

  useIsRewardRedeemable(id: string): boolean {
    const allRewardItems = this.useAllRewardItems();
    const rewardItem = useMemo(() => allRewardItems.find(item => item.id === id), [id]);
    const { cumulativePoints } = this.usePoints();
    const redeemableBasedOnTierRank = this.useIsRedeemableByTier(id);

    return useMemo(() => {
      if (!rewardItem) {
        return false;
      }
      // Checking for points
      const hasEnoughPointsToRedeem = cumulativePoints >= rewardItem.costInPoints;

      if (!hasEnoughPointsToRedeem) {
        return false;
      }

      return redeemableBasedOnTierRank;
    }, [rewardItem, cumulativePoints, redeemableBasedOnTierRank]);
  },
  useIsRedeemableByTier(id: string): boolean {
    const isTiersEnabled = this.useIsTiersEnabled();
    const { previousTier } = this.useTiers();
    const tiersContainingReward = this.useTiersContainingReward(id);

    return useMemo(() => {
      // If tiers are not enabled and customer has enough points, they can redeem
      if (!isTiersEnabled) {
        return true;
      }

      // If there are tiers assigned to this reward and the user doesn't have a tier, we can disable
      if (tiersContainingReward?.length && !previousTier) {
        return false;
      }

      // If there aren't any tiers assigned to this reward and the user has a tier, they can redeem

      return tiersContainingReward?.length ? previousTier?.tierRank! >= tiersContainingReward[0].tierRank : true;
    }, [isTiersEnabled, tiersContainingReward, previousTier]);
  },
};

export const LoyaltyStoreTest = {
  initialState: useLoyaltyStore.getState(),
  reset() {
    useLoyaltyStore.setState(this.initialState);
  },
  get() {
    return useLoyaltyStore.getState();
  },
  actions() {
    return useLoyaltyStore.getState().actions;
  },
  // TODO: Move params into an object
  initialize(
    loyalty: LoyaltyApi.Program.Type | undefined,
    upcomingOrder?: PurchaseStateAndDelivery | null,
    latestOrder?: ISmartrrOrderFromDb | null
  ) {
    this.reset();
    this.get().internal.initializeLoyalty(loyalty);
    this.get().actions.order.updateUpcoming(upcomingOrder ?? null);
    this.get().actions.order.updatePurchased(latestOrder ?? null);
  },
  initializeTiers(
    tieredPoints?: number | undefined,
    cumulativePoints?: number | undefined,
    yearlyTieredPoints?: YearlyTieredPoints[]
  ) {
    if (tieredPoints) {
      this.get().actions.points.updateTieredPoints(tieredPoints);
    }
    if (cumulativePoints) {
      this.get().actions.points.updateCumulativePoints(cumulativePoints);
    }
    this.get().actions.tiers.updateTier(yearlyTieredPoints);
  },
};
